import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, Link } from 'react-router-dom';
import { useHistory } from 'react-router';
import { Trans, useTranslation } from 'react-i18next';
import axios from 'axios';
import { Helmet } from 'react-helmet';
import { parse } from 'query-string';
import ReCAPTCHA from 'react-google-recaptcha';
import { useAlert } from 'component/alert/provider';
import { useClassnames } from 'hook/use-classnames';
import cancelToken from 'component/core/cancel-token';
import UI from 'component/ui';
import Button from 'component/button';
import MailSent from 'component/mail-sent';
import Error from 'component/error';
import Form, { useRegistry } from 'component/form';
import { IPayload, IError } from 'component/form/types';
import FormInput from 'component/form/input';
import { ErrorCode } from 'component/api/types/api/account/registration/post/code-400';
import { set, reset } from 'store/reducers/user/actions';
import { addCart } from 'store/reducers/cart/actions';
import { IStore } from 'store/reducers/types/reducers';
import { key as keyUser } from 'store/reducers/user/reducer';
import style from './style.pcss';
import useScript from 'hook/use-script';
// @ts-ignore
import policy from './docs/policy.pdf';
import api from 'src/api';
import { CartItemsStore } from 'src/store/reducers/cart/types/reducer';
import { YandexTasksListData } from 'src/api/tasks/types';
import { resetTask, setTask } from 'src/store/reducers/task/actions';
import { CartItem } from 'src/api/cart/types';

const Registration = () => {
    const cn = useClassnames(style);
    const history = useHistory();
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();
    const { show } = useAlert();
    const registry = useRegistry();
    const qs = useMemo(() => parse(history.location.search), [history.location.search]);
    const tokenRegistration = useMemo(cancelToken.create, []);

    const defaultEmail = useMemo<string | null>(() => (typeof qs.email === 'string' ? qs.email : null), [qs.email]);
    const isAuth = useSelector<IStore, boolean>((store) => !!store[keyUser].id);

    const [siteKey, setSiteKey] = useState<string>('');
    const [isNotBot, setIsNotBot] = useState<boolean>(false);
    const [pending, setPending] = useState<boolean>(false);
    const [isFormValid, setIsFormValid] = useState<boolean>(false);
    const [mailSent, setMailSent] = useState<boolean>(false);
    const [errorCode, setErrorCode] = useState<ErrorCode | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [errors, setErrors] = useState<Array<IError> | null>(null);

    const [cartItemsListPage, setCartItemsListPage] = useState<number>(1);
    const [isCartItemsListLoading, setIsCartItemsListLoading] = useState<boolean>(false);
    const [isCartItemsListLoadMore, setIsCartItemsListLoadMore] = useState<boolean>(false);
    const [cartItemsList, setCartItemsList] = useState<Array<CartItem>>([]);
    const [isLoadingPersonInfo, setIsLoadingPersonInfo] = useState<boolean>(false);

    useEffect(() => {
        if (localStorage.getItem('jwt_token') && isLoadingPersonInfo) {
            setTimeout(async () => {
                const payload = await api.accounts.getAccountRetrieve();
                dispatch(set(payload.data));
            }, 300);
            setPending(false);
            setIsLoadingPersonInfo(false);
        }
    }, [isLoadingPersonInfo]);

    useEffect(() => {
        if (isCartItemsListLoading || isCartItemsListLoadMore) {
            api.cart.getCartItemsList({
                pageNumber: cartItemsListPage
            })
            .then((resp) => {
                const filteredList: CartItemsStore = {
                    count: 0,
                    items: []
                };

                setCartItemsList((prev) => isCartItemsListLoadMore ?
                    [...prev, ...resp.data.results] : resp.data.results);
                if (!resp.data.next) {
                    const newList = isCartItemsListLoadMore ? [...cartItemsList, ...resp.data.results].map((item) => filteredList.items.push(item))
                        : resp.data.results.map((item) => filteredList.items.push(item));
                    filteredList.count = resp.data.count;
                    dispatch(addCart(filteredList));
                    setIsCartItemsListLoading(false);
                    setIsCartItemsListLoadMore(false);
                } else {
                    setIsCartItemsListLoading(false);
                    setIsCartItemsListLoadMore(false);

                    setCartItemsListPage((prev) => prev + 1);
                    setIsCartItemsListLoadMore(true);
                }
            });
        }
    }, [isCartItemsListLoading, isCartItemsListLoadMore]);

    useEffect(() => {
        if (process.env.SITE_KEY) {
            setSiteKey(process.env.SITE_KEY);
        }
    }, []);

    useEffect(() => {
        return () => {
            tokenRegistration.remove();
        };
    }, []);

    useEffect(() => {
        const promise = useScript('https://yastatic.net/s3/passport-sdk/autofill/v1/sdk-suggest-with-polyfills-latest.js');

        promise.then(
        () => {
            authYa();
        },
        // tslint:disable-next-line: no-shadowed-variable
        (error: { message: string; }) => {
            alert(`Ошибка: ${error.message}`);
        }
        );
    }, []);

    const getUserInfo = async (access_token: string) => {
        if(!pending) {
            setPending(true);

            api.auth.authYandexCreate(access_token)
                .then((resp) => {
                    localStorage.setItem('jwt_token', resp.data.access_token);
                    localStorage.setItem('jwt_token_refresh', resp.data.refresh_token);

                    setCartItemsListPage(1);
                    setIsCartItemsListLoading(true);

                    const taskFilter: YandexTasksListData = {
                        seen: false
                    };

                    api.tasks.getYandexTasksList(undefined, taskFilter)
                        .then((payload) => {
                            dispatch(setTask(payload.data.count));
                        })
                        .catch(() => {
                            dispatch(resetTask());
                        });

                    setIsLoadingPersonInfo(true);
                })
                .catch((payload) => {
                    if(!axios.isCancel(payload)) {
                        setError(payload.message);
                        setErrorCode(payload.error_code);
                        setPending(false);

                        show(payload.message, 'danger');

                        if(payload.errors) {
                            setErrors(payload.errors);
                        }

                        dispatch(reset());
                    }
                });
    }
    };

    const authYa = useCallback(() => {
        // @ts-ignore
        YaAuthSuggest.init({
            client_id: process.env.YANDEX_API_CLIENT_ID,
            response_type: 'token',
            redirect_uri: `${location.origin}/auth-ya/suggest/token`
            },
            `${location.origin}`, {
                view: 'button',
                parentId: 'auth-container',
                buttonView: 'main',
                buttonTheme: 'light',
                buttonSize: 'm',
                buttonBorderRadius: 4
             }
         )
         .then((result: {handler: Function}) => {
            return result.handler();
         })
         .then((data: {'access_token': string; 'token_type': string; 'expires_in': string; 'extra_data': {}}) => {
            getUserInfo(data.access_token);
         })
         // tslint:disable-next-line: no-shadowed-variable
         .catch((error: unknown) => {
            alert(`Что-то пошло не так: ${JSON.stringify(error)}`);
         });
    }, [pending]);

    const onChangeValidState = useCallback((payload: boolean) => {
        if(payload !== isFormValid) {
            setIsFormValid(payload);
        }
    }, [isFormValid]);

    const onSubmit = useCallback((formData: IPayload) => {
        if(isFormValid) {
            setPending(true);

            if(errorCode) {
                setErrorCode(null);
            }

            if(error) {
                setError(null);
            }

            if(errors) {
                setErrors(null);
            }

            api.auth.authRegistrationCreate(
                formData.email,
                `9${formData.phone_number}`
            )
                .then(() => {
                    // @ts-ignore
                    ym(67967041, 'reachGoal', 'rega');
                    setMailSent(true);
                })
                .catch((payload) => {
                    setPending(false);
                    setErrorCode(payload.response.status);
                    switch (payload.response.status) {
                        case 400:
                            if (payload.response.data.phone) {
                                setErrors([{ key: 'phone_number', message: t('route.registration.error.number_error', {maxLength: 10}) }]);
                            }
                            break;
                        case 409:
                            setErrors([{ key: 'email', message: t('route.registration.error.already_registered') }]);
                            break;
                        case 412:
                            setErrors([{ key: 'email', message: t('route.registration.error.not_allowed') }]);
                            break;
                        case 500:
                            setError(t('route.registration.error.server_error'));
                            break;
                    }
                });
        }
    }, [isFormValid, error, errors, errorCode]);

    const onChange = useCallback(() => {
        setIsNotBot(true);
    }, []);

    const elError = useMemo(() => {
        if(error) {
            return (
                <Error className={cn('registration__error')}>
                    {error}
                </Error>
            );
        }
    }, [error, registry.form.getPayload().email, errorCode, i18n.language]);

    const elContent = () => {
        if(mailSent) {
            return <MailSent resendMethod="email" email={registry.form.getPayload().email} />;
        }

        return (
            <div className={cn('registration__content')}>
                <h2 className={cn('registration__header')}>
                    {t('route.registration.header')}
                    <Link
                        title={t('route.registration.link.login')}
                        to="/login"
                        className={cn('registration__link-login')}
                    >
                        {t('route.registration.link.login')}
                    </Link>
                </h2>
                <Form
                    registry={registry.form}
                    className={cn('registration__form')}
                    onSubmit={onSubmit}
                    onChangeValidity={onChangeValidState}
                    errors={errors}
                >
                    <FormInput
                        className={cn('registration__input')}
                        registry={registry.field}
                        direction="column"
                        name="email"
                        type="email"
                        required={true}
                        tabIndex={1}
                        defaultValue={defaultEmail}
                    >
                        {t('route.registration.input.email.label')}
                    </FormInput>
                    <FormInput
                        className={cn('registration__input')}
                        registry={registry.field}
                        direction="column"
                        name="phone_number"
                        required={true}
                        tabIndex={1}
                        placeholder="XX) XXX-XX-XX"
                        onlyNumbers={true}
                        requiredLength={9}
                        firstSymbol={9}
                        maxLength={9}
                        prefix={'+7 9'}
                    >
                        {t('route.registration.input.phone_number.label')}
                    </FormInput>
                    <span style={{display: 'block', color: '#ff5487', fontSize: '14px', marginTop: '10px'}}>{t('route.registration.guarantee')}</span>
                    <div className={cn('registration__accept-policy')}>
                        <Trans i18n={i18n} i18nKey="route.registration.accept-policy">
                            By signing up you're agreeing to our
                            <Link
                                className={cn('registration__link-label')}
                                to="/terms-and-conditions"
                            >
                                Terms of Service
                            </Link>
                            and
                            <a
                                href={policy}
                                target="_blank"
                                className={cn('registration__link-label')}
                            >
                                Privacy Policy
                            </a>
                            .
                        </Trans>
                    </div>
                    {elError}
                    <div className={cn('registration__recaptcha')}>
                    <ReCAPTCHA
                        sitekey={siteKey}
                        onChange={onChange}
                    />
                    </div>
                    <Button
                        type="submit"
                        className={cn('registration__button-submit')}
                        isLoading={pending}
                        disabled={!isFormValid || pending || !isNotBot}
                    >
                        {t('route.registration.button.sign-up')}
                    </Button>
                </Form>
                <div id="auth-container" className={cn('registration__auth-container')} />
            </div>
        );
    };

    if(isAuth) {
        return <Redirect to="/subscriptions" />;
    }

    return (
        <UI.Main className={cn('registration__ui-main')}>
            <Helmet
                title={t('helmet.title.registration')}
                meta={[{
                    name   : 'document-state',
                    content: 'static'
                }]}
            />

            <UI.Box padding={true} className={cn('registration__ui-box')}>
                {elContent()}
            </UI.Box>
        </UI.Main>
    );
};

export default Registration;
